import React, { useState, useEffect } from 'react';
import { AdminRoute } from '../components/ProtectedRoute';
import { adminAPI, collectionRequestAPI, collectorAPI } from '../services/api';
import api from '../services/api';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './AdminPage.css';

interface User {
  id: number;
  username: string;
  email: string;
  role: 'user' | 'admin' | 'collector';
  created_at: string;
  updated_at: string;
  is_active: boolean;
  is_verified: boolean;
  full_name?: string;
  address_street?: string;
  address_detail?: string;
  address_zipcode?: string;
  phone?: string;
}

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  newUsersThisMonth: number;
  adminCount: number;
  collectorCount: number;
  totalCollections: number;
  activeCollections: number;
  totalAnalyses: number;
  systemUptime: string;
}

interface ActivityLog {
  id: number;
  timestamp: string;
  type: 'user' | 'system' | 'security';
  description: string;
  user_id?: number;
}

interface CollectionRequest {
  id: number;
  waste_type: string;
  description: string;
  collection_address: string;
  collection_date?: string;
  urgency: string;
  status: string;
  estimated_weight?: number;
  estimated_volume?: number;
  contact_phone?: string;
  special_instructions?: string;
  created_at: string;
  updated_at: string;
  user_id: number;
  assigned_collector_id?: number;
  actual_volume?: number;
  completion_notes?: string;
  completion_photos?: string[];
  final_weight?: number;
  review_notes?: string;
  user: {
    username: string;
    email: string;
  };
  assigned_collector?: {
    id: number;
    employee_id: string;
    user: {
      username: string;
    };
  };
}

interface Collector {
  id: number;
  employee_id: string;
  user: {
    username: string;
  };
  status: string;
  is_available: boolean;
}

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'users' | 'collections' | 'recurring' | 'analytics' | 'billing' | 'logs'>('dashboard');
  const [users, setUsers] = useState<User[]>([]);
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    activeUsers: 0,
    newUsersThisMonth: 0,
    adminCount: 0,
    collectorCount: 0,
    totalCollections: 0,
    activeCollections: 0,
    totalAnalyses: 0,
    systemUptime: ''
  });
  const [activityLogs, setActivityLogs] = useState<ActivityLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | 'user' | 'admin' | 'collector'>('all');
  
  // Collection requests state
  const [collectionRequests, setCollectionRequests] = useState<CollectionRequest[]>([]);
  const [collectors, setCollectors] = useState<Collector[]>([]);
  const [selectedRequest, setSelectedRequest] = useState<CollectionRequest | null>(null);
  const [assigningCollector, setAssigningCollector] = useState(false);
  
  console.log('Current collectionRequests state:', collectionRequests);
  
  // Photo review state
  const [reviewingRequest, setReviewingRequest] = useState<CollectionRequest | null>(null);
  const [finalWeight, setFinalWeight] = useState<number | ''>('');
  const [reviewNotes, setReviewNotes] = useState('');

  // Recurring collections state
  const [recurringSchedules, setRecurringSchedules] = useState<any[]>([]);
  const [todayRecurringCollections, setTodayRecurringCollections] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showCreateSchedule, setShowCreateSchedule] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load real data from API
      console.log('Loading dashboard data...');
      const [statsData, usersData, logsData] = await Promise.all([
        adminAPI.getSystemStats(),
        adminAPI.getUsers(),
        adminAPI.getActivityLogs()
      ]);
      
      console.log('Stats data:', statsData);
      console.log('Users data:', usersData);
      console.log('Logs data:', logsData);
      
      setStats(statsData);
      setUsers(usersData);
      setActivityLogs(logsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const usersData = await adminAPI.getUsers({
        search: userFilter || undefined,
        role: roleFilter === 'all' ? undefined : roleFilter
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'collections') {
      console.log('Loading collections data...');
      loadCollectionRequests();
      loadCollectors();
    } else if (activeTab === 'recurring') {
      loadRecurringSchedules();
      loadTodayRecurringCollections();
    }
  }, [activeTab, userFilter, roleFilter]);

  const loadCollectionRequests = async () => {
    try {
      // 관리자용 전체 요청 조회 API 사용 - axios 사용
      const response = await api.get('/api/sslmo/auth/collection-requests/admin/all', {
        params: {
          page: 1,
          size: 50
        }
      });
      
      console.log('Collection requests data:', response.data);
      console.log('Data length:', response.data.length);
      setCollectionRequests(response.data);
    } catch (error) {
      console.error('Failed to load collection requests:', error);
    }
  };

  const loadCollectors = async () => {
    try {
      const data = await collectorAPI.getCollectors();
      console.log('Collectors data:', data);
      setCollectors(data);
    } catch (error) {
      console.error('Failed to load collectors:', error);
    }
  };

  const loadRecurringSchedules = async () => {
    try {
      const response = await api.get('/api/sslmo/auth/admin/recurring-schedules');
      console.log('Recurring schedules data:', response.data);
      
      // API 응답이 HTML인지 확인하고 적절히 처리
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        console.error('API returned HTML instead of JSON data');
        setRecurringSchedules([]);
        return;
      }
      
      // 배열인지 확인
      if (Array.isArray(response.data)) {
        setRecurringSchedules(response.data);
      } else if (response.data && Array.isArray(response.data.schedules)) {
        setRecurringSchedules(response.data.schedules);
      } else {
        console.error('Unexpected data format:', response.data);
        setRecurringSchedules([]);
      }
    } catch (error) {
      console.error('Failed to load recurring schedules:', error);
      setRecurringSchedules([]);
    }
  };

  const loadTodayRecurringCollections = async () => {
    try {
      const response = await api.get('/api/sslmo/auth/admin/recurring-schedules/today');
      console.log('Today recurring collections data:', response.data);
      
      // API 응답이 HTML인지 확인하고 적절히 처리
      if (typeof response.data === 'string' && response.data.includes('<!doctype html>')) {
        console.error('API returned HTML instead of JSON data');
        setTodayRecurringCollections([]);
        return;
      }
      
      // 배열인지 확인
      if (Array.isArray(response.data)) {
        setTodayRecurringCollections(response.data);
      } else if (response.data && Array.isArray(response.data.collections)) {
        setTodayRecurringCollections(response.data.collections);
      } else {
        console.error('Unexpected data format:', response.data);
        setTodayRecurringCollections([]);
      }
    } catch (error) {
      console.error('Failed to load today recurring collections:', error);
      setTodayRecurringCollections([]);
    }
  };

  const handleUserRoleChange = async (userId: number, newRole: 'user' | 'admin' | 'collector') => {
    try {
      console.log('Changing user role:', { userId, newRole });
      const result = await adminAPI.updateUserRole(userId, newRole);
      console.log('Role change result:', result);
      
      // Show success message
      alert(result.message || '역할이 성공적으로 변경되었습니다');
      
      // Reload users and activity logs
      await loadUsers();
      const logsData = await adminAPI.getActivityLogs();
      setActivityLogs(logsData);
    } catch (error: any) {
      console.error('Failed to change user role:', error);
      console.error('Error details:', error.response);
      alert(error.response?.data?.detail || '역할 변경에 실패했습니다');
    }
  };

  const handleAssignCollector = async (requestId: number, collectorId: number) => {
    try {
      setAssigningCollector(true);
      const response = await api.patch(`/api/sslmo/auth/collection-requests/${requestId}/assign`, {
        collector_id: collectorId
      });
      
      await loadCollectionRequests();
      setSelectedRequest(null);
      alert('작업자가 성공적으로 배정되었습니다');
    } catch (error: any) {
      console.error('Failed to assign collector:', error);
      alert(error.response?.data?.detail || '작업자 배정에 실패했습니다');
    } finally {
      setAssigningCollector(false);
    }
  };

  const handleReviewCompletion = async (requestId: number) => {
    try {
      const response = await api.patch(`/api/sslmo/auth/collection-requests/${requestId}/review`, {
        final_weight: finalWeight ? Number(finalWeight) : null,
        review_notes: reviewNotes || null
      });
      
      await loadCollectionRequests();
      setReviewingRequest(null);
      setFinalWeight('');
      setReviewNotes('');
      alert('검토가 완료되었습니다');
    } catch (error: any) {
      console.error('Failed to review completion:', error);
      alert(error.response?.data?.detail || '검토 완료 처리에 실패했습니다');
    }
  };

  const handleUserToggleActive = async (userId: number) => {
    try {
      await adminAPI.toggleUserStatus(userId);
      
      // Reload users and activity logs
      await loadUsers();
      const logsData = await adminAPI.getActivityLogs();
      setActivityLogs(logsData);
    } catch (error: any) {
      console.error('Failed to toggle user status:', error);
      alert(error.response?.data?.detail || '상태 변경에 실패했습니다');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(userFilter.toLowerCase()) ||
                         user.email.toLowerCase().includes(userFilter.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('ko-KR');
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}일 전`;
    if (diffHours > 0) return `${diffHours}시간 전`;
    return '방금 전';
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin': return '관리자';
      case 'collector': return '수거작업자';
      default: return '사용자';
    }
  };

  const getNextRole = (currentRole: string): 'user' | 'admin' | 'collector' => {
    // 역할 순환: user -> collector -> admin -> user
    switch (currentRole) {
      case 'user': return 'collector';
      case 'collector': return 'admin';
      case 'admin': return 'user';
      default: return 'user';
    }
  };

  if (loading) {
    return (
      <AdminRoute>
        <div className="admin-page">
          <div className="loading-container">
            <LoadingSpinner size="large" />
            <p>관리자 대시보드를 로딩 중...</p>
          </div>
        </div>
      </AdminRoute>
    );
  }

  return (
    <AdminRoute>
      <div className="admin-page">
        <header className="admin-header">
          <h1>관리자 대시보드</h1>
          <p>시스템 관리 및 모니터링을 위한 관리자 전용 페이지입니다.</p>
        </header>

        <nav className="admin-nav">
          <div className="nav-buttons">
            {[
              { key: 'dashboard', label: '대시보드' },
              { key: 'users', label: '사용자 관리' },
              { key: 'collections', label: '컬렉션 관리' },
              { key: 'recurring', label: '정기 수거' },
              { key: 'analytics', label: 'AI 분석' },
              { key: 'billing', label: '요금 정책' },
              { key: 'logs', label: '활동 로그' }
            ].map(({ key, label }) => (
              <button
                key={key}
                className={`nav-button ${activeTab === key ? 'active' : ''}`}
                onClick={() => setActiveTab(key as any)}
              >
                {label}
              </button>
            ))}
          </div>
        </nav>

        <main className="admin-content">
          {activeTab === 'dashboard' && (
            <>
              <div className="stats-grid">
                <div className="stat-card">
                  <h3>전체 사용자</h3>
                  <div className="stat-value">{stats.totalUsers}</div>
                  <div className="stat-info">활성: {stats.activeUsers}명</div>
                </div>
                <div className="stat-card">
                  <h3>이번 달 신규</h3>
                  <div className="stat-value">{stats.newUsersThisMonth}</div>
                  <div className="stat-info">신규 가입자</div>
                </div>
                <div className="stat-card">
                  <h3>관리자</h3>
                  <div className="stat-value">{stats.adminCount}</div>
                  <div className="stat-info">관리자 계정</div>
                </div>
                <div className="stat-card">
                  <h3>수거작업자</h3>
                  <div className="stat-value">{stats.collectorCount}</div>
                  <div className="stat-info">작업자 계정</div>
                </div>
                <div className="stat-card">
                  <h3>전체 수거 요청</h3>
                  <div className="stat-value">{stats.totalCollections}</div>
                  <div className="stat-info">진행중: {stats.activeCollections}건</div>
                </div>
                <div className="stat-card">
                  <h3>완료된 분석</h3>
                  <div className="stat-value">{stats.totalAnalyses}</div>
                  <div className="stat-info">AI 분석 완료</div>
                </div>
              </div>

              <div className="recent-activity">
                <h2>최근 활동</h2>
                <div className="activity-list">
                  {activityLogs.slice(0, 10).map(log => (
                    <div key={log.id} className="activity-item">
                      <span className="activity-time">{getTimeAgo(log.timestamp)}</span>
                      <span className="activity-desc">{log.description}</span>
                      <span className={`activity-type type-${log.type}`}>{log.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === 'users' && (
            <div className="management-section">
              <h2>사용자 관리</h2>
              <div className="section-content">
                <div className="user-filters">
                  <input
                    type="text"
                    placeholder="사용자명 또는 이메일 검색..."
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    className="filter-input"
                  />
                  <select
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value as any)}
                    className="filter-select"
                  >
                    <option value="all">모든 역할</option>
                    <option value="user">사용자</option>
                    <option value="admin">관리자</option>
                    <option value="collector">수거작업자</option>
                  </select>
                </div>
                
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>사용자명</th>
                      <th>이메일</th>
                      <th>이름</th>
                      <th>역할</th>
                      <th>가입일</th>
                      <th>상태</th>
                      <th>인증</th>
                      <th>작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => (
                      <tr key={user.id}>
                        <td>{user.username}</td>
                        <td>{user.email}</td>
                        <td>{user.full_name || '-'}</td>
                        <td>
                          <span className={`user-role role-${user.role}`}>
                            {getRoleLabel(user.role)}
                          </span>
                        </td>
                        <td>{formatDate(user.created_at)}</td>
                        <td>
                          <span className={user.is_active ? 'text-success' : 'text-danger'}>
                            {user.is_active ? '활성' : '비활성'}
                          </span>
                        </td>
                        <td>
                          <span className={user.is_verified ? 'text-success' : 'text-warning'}>
                            {user.is_verified ? '인증됨' : '미인증'}
                          </span>
                        </td>
                        <td>
                          <div className="user-actions">
                            <button
                              className="action-btn btn-edit"
                              onClick={() => handleUserRoleChange(user.id, getNextRole(user.role))}
                              title="역할 변경"
                            >
                              {getRoleLabel(getNextRole(user.role))}로 변경
                            </button>
                            <button
                              className="action-btn btn-delete"
                              onClick={() => handleUserToggleActive(user.id)}
                            >
                              {user.is_active ? '비활성화' : '활성화'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'collections' && (
            <div className="management-section">
              <h2>수거 요청 관리</h2>
              <div className="section-content">
                <div className="collections-grid">
                  {(() => {
                    console.log('Rendering collectionRequests:', collectionRequests);
                    return null;
                  })()}
                  {collectionRequests.length === 0 ? (
                    <div className="no-requests">수거 요청이 없습니다</div>
                  ) : (
                    collectionRequests.map((request) => (
                      <div key={request.id} className="collection-card">
                        <div className="collection-header">
                          <span className="collection-id">#{request.id}</span>
                          <span className={`status-badge ${request.status}`}>
                            {request.status === 'pending' && '대기중'}
                            {request.status === 'confirmed' && '확인됨'}
                            {request.status === 'in_progress' && '진행중'}
                            {request.status === 'completed' && '완료됨'}
                            {request.status === 'cancelled' && '취소됨'}
                          </span>
                          <span className={`urgency-badge ${request.urgency}`}>
                            {request.urgency === 'low' && '낮음'}
                            {request.urgency === 'medium' && '보통'}
                            {request.urgency === 'high' && '높음'}
                            {request.urgency === 'urgent' && '긴급'}
                          </span>
                        </div>
                        
                        <div className="collection-content">
                          <div className="user-info">
                            <strong>요청자:</strong> {request.user?.username || `사용자 ID: ${request.user_id}`} {request.user?.email && `(${request.user.email})`}
                          </div>
                          <div className="address">
                            <strong>📍 주소:</strong> {request.collection_address}
                          </div>
                          <div className="description">
                            <strong>설명:</strong> {request.description}
                          </div>
                          {request.estimated_volume && (
                            <div className="volume">
                              <strong>예상 용량:</strong> {request.estimated_volume}L
                            </div>
                          )}
                          {request.actual_volume && (
                            <div className="actual-volume">
                              <strong>실제 용량:</strong> {request.actual_volume}L
                            </div>
                          )}
                          {request.contact_phone && (
                            <div className="contact">
                              <strong>연락처:</strong> {request.contact_phone}
                            </div>
                          )}
                          {request.assigned_collector && (
                            <div className="assigned-collector">
                              <strong>배정된 작업자:</strong> {request.assigned_collector.employee_id} {request.assigned_collector.user?.username && `(${request.assigned_collector.user.username})`}
                            </div>
                          )}
                          <div className="created-date">
                            <strong>생성일:</strong> {formatDate(request.created_at)}
                          </div>
                        </div>

                        <div className="collection-actions">
                          {request.status === 'pending' && !request.assigned_collector_id && (
                            <button 
                              onClick={() => setSelectedRequest(request)}
                              className="assign-btn"
                            >
                              작업자 배정
                            </button>
                          )}
                          
                          {request.status === 'pending' && request.assigned_collector_id && (
                            <button 
                              onClick={() => {
                                // 확인 처리 API 호출
                                console.log('Confirm request:', request.id);
                              }}
                              className="confirm-btn"
                            >
                              요청 확인
                            </button>
                          )}

                          {request.status === 'completed' && (
                            <button 
                              onClick={() => setReviewingRequest(request)}
                              className="review-btn"
                            >
                              완료 검토
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'analytics' && (
            <div className="management-section">
              <h2>AI 분석 성과</h2>
              <div className="section-content">
                <div className="chart-placeholder">
                  AI 모델 성능 지표 및 분석 통계
                  <br />
                  (구현 예정: 정확도, 처리 시간, 모델 버전 관리)
                </div>
              </div>
            </div>
          )}

          {activeTab === 'recurring' && (
            <div className="management-section">
              <h2>정기 수거 관리</h2>
              <div className="section-content">
                <div className="recurring-controls">
                  <button 
                    className="btn-primary"
                    onClick={() => setShowCreateSchedule(true)}
                  >
                    + 정기 수거 등록
                  </button>
                  <button 
                    className="btn-secondary"
                    onClick={async () => {
                      try {
                        await api.post('/api/sslmo/auth/admin/recurring-schedules/generate-today');
                        alert('오늘의 정기 수거 요청이 생성되었습니다.');
                        loadTodayRecurringCollections();
                                              } catch (error) {
                          console.error('Failed to generate today requests:', error);
                          alert('정기 수거 요청 생성에 실패했습니다.');
                        }
                    }}
                  >
                    오늘 요청 생성
                  </button>
                </div>

                <div className="recurring-sections">
                  <div className="section">
                    <h3>오늘의 정기 수거</h3>
                    <div className="today-collections">
                      {!Array.isArray(todayRecurringCollections) || todayRecurringCollections.length === 0 ? (
                        <div className="no-data">오늘의 정기 수거가 없습니다</div>
                      ) : (
                        todayRecurringCollections.map((collection) => (
                          <div key={collection.schedule_id} className="collection-item">
                            <div className="collection-info">
                              <div className="user-info">
                                <strong>{collection.user_name}</strong>
                                <span className="email">{collection.user_email}</span>
                              </div>
                              <div className="collection-details">
                                <span className="time">{collection.collection_time}</span>
                                <span className="address">{collection.collection_address}</span>
                              </div>
                              <div className="status-info">
                                {collection.request_created ? (
                                  <span className="status-created">요청 생성됨 (ID: {collection.request_id})</span>
                                ) : (
                                  <span className="status-pending">요청 미생성</span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="section">
                    <h3>정기 수거 스케줄 목록</h3>
                    <div className="schedules-list">
                      {!Array.isArray(recurringSchedules) || recurringSchedules.length === 0 ? (
                        <div className="no-data">등록된 정기 수거 스케줄이 없습니다</div>
                      ) : (
                        recurringSchedules.map((schedule) => (
                          <div key={schedule.id} className="schedule-item">
                            <div className="schedule-info">
                              <div className="schedule-header">
                                <strong>스케줄 #{schedule.id}</strong>
                                <span className={`status ${schedule.is_active ? 'active' : 'inactive'}`}>
                                  {schedule.is_active ? '활성' : '비활성'}
                                </span>
                              </div>
                              <div className="schedule-details">
                                <span>사용자 ID: {schedule.user_id}</span>
                                <span>요일: {['월', '화', '수', '목', '금', '토', '일'][schedule.day_of_week]}</span>
                                <span>시간: {schedule.time_of_day}</span>
                                <span>주기: {schedule.frequency}</span>
                              </div>
                              <div className="schedule-stats">
                                <span>생성된 요청: {schedule.total_requests_created}건</span>
                                {schedule.last_request_created && (
                                  <span>마지막 생성: {formatDate(schedule.last_request_created)}</span>
                                )}
                              </div>
                            </div>
                            <div className="schedule-actions">
                              <button 
                                className="btn-toggle"
                                onClick={async () => {
                                  try {
                                    await api.patch(`/api/sslmo/auth/admin/recurring-schedules/${schedule.id}/toggle`);
                                    loadRecurringSchedules();
                                                                      } catch (error) {
                                      console.error('Failed to toggle schedule:', error);
                                      alert('스케줄 상태 변경에 실패했습니다.');
                                    }
                                }}
                              >
                                {schedule.is_active ? '비활성화' : '활성화'}
                              </button>
                              <button 
                                className="btn-delete"
                                onClick={async () => {
                                  if (window.confirm('정기 수거 스케줄을 삭제하시겠습니까?')) {
                                    try {
                                      await api.delete(`/api/sslmo/auth/admin/recurring-schedules/${schedule.id}`);
                                      loadRecurringSchedules();
                                    } catch (error) {
                                      console.error('Failed to delete schedule:', error);
                                      alert('스케줄 삭제에 실패했습니다.');
                                    }
                                  }
                                }}
                              >
                                삭제
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'billing' && (
            <div className="management-section">
              <h2>요금 정책 관리</h2>
              <div className="section-content">
                <div className="chart-placeholder">
                  요금제 설정 및 사용량 관리
                  <br />
                  (구현 예정: 요금제 설정, 사용량 모니터링, 청구 관리)
                </div>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="management-section">
              <h2>활동 로그</h2>
              <div className="section-content">
                <div className="activity-list full-list">
                  {activityLogs.map(log => (
                    <div key={log.id} className="activity-item">
                      <span className="activity-time">{formatDate(log.timestamp)}</span>
                      <span className="activity-desc">{log.description}</span>
                      <span className={`activity-type type-${log.type}`}>{log.type}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </main>

        {/* 작업자 배정 모달 */}
        {selectedRequest && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>작업자 배정 - 요청 #{selectedRequest.id}</h2>
                <button onClick={() => setSelectedRequest(null)} className="close-btn">×</button>
              </div>
              
              <div className="modal-content">
                <div className="request-summary">
                  <h3>요청 요약</h3>
                  <p><strong>요청자:</strong> {selectedRequest.user?.username || `사용자 ID: ${selectedRequest.user_id}`}</p>
                  <p><strong>주소:</strong> {selectedRequest.collection_address}</p>
                  <p><strong>설명:</strong> {selectedRequest.description}</p>
                  <p><strong>예상 무게:</strong> {selectedRequest.estimated_weight || '미지정'}kg</p>
                </div>

                <div className="available-collectors">
                  <h3>사용 가능한 작업자</h3>
                  {collectors.filter(c => c.is_available).length === 0 ? (
                    <div className="no-collectors">사용 가능한 작업자가 없습니다</div>
                  ) : (
                    <div className="collectors-list">
                      {collectors.filter(c => c.is_available).map((collector) => (
                        <div key={collector.id} className="collector-item">
                          <div className="collector-info">
                            <div className="collector-name">
                              <strong>{collector.employee_id}</strong> {collector.user?.username && `- ${collector.user.username}`}
                            </div>
                            <div className="collector-status">
                              상태: {collector.status === 'on_duty' ? '근무중' : collector.status}
                            </div>
                          </div>
                          <button
                            onClick={() => handleAssignCollector(selectedRequest.id, collector.id)}
                            disabled={assigningCollector}
                            className="assign-collector-btn"
                          >
                            {assigningCollector ? '배정 중...' : '배정'}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 완료 검토 모달 */}
        {reviewingRequest && (
          <div className="modal-overlay">
            <div className="modal review-modal">
              <div className="modal-header">
                <h2>완료 검토 - 요청 #{reviewingRequest.id}</h2>
                <button onClick={() => {
                  setReviewingRequest(null);
                  setFinalWeight('');
                  setReviewNotes('');
                }} className="close-btn">×</button>
              </div>
              
              <div className="modal-content">
                <div className="request-summary">
                  <h3>작업 정보</h3>
                  <p><strong>요청자:</strong> {reviewingRequest.user?.username || `사용자 ID: ${reviewingRequest.user_id}`}</p>
                  <p><strong>주소:</strong> {reviewingRequest.collection_address}</p>
                  <p><strong>설명:</strong> {reviewingRequest.description}</p>
                  <p><strong>예상 용량:</strong> {reviewingRequest.estimated_volume || '미지정'}L</p>
                  <p><strong>작업자 측정 용량:</strong> {reviewingRequest.actual_volume || '미입력'}L</p>
                  {reviewingRequest.completion_notes && (
                    <p><strong>작업자 메모:</strong> {reviewingRequest.completion_notes}</p>
                  )}
                </div>

                {reviewingRequest.completion_photos && reviewingRequest.completion_photos.length > 0 && (
                  <div className="completion-photos">
                    <h3>완료 증빙 사진</h3>
                    <div className="photos-grid">
                      {reviewingRequest.completion_photos.map((photoUrl, index) => (
                        <div key={index} className="photo-container">
                          <img 
                            src={`http://localhost:8002${photoUrl}`} 
                            alt={`완료 사진 ${index + 1}`}
                            className="completion-photo"
                            onClick={() => window.open(`http://localhost:8002${photoUrl}`, '_blank')}
                          />
                          <div className="photo-label">사진 {index + 1}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <form onSubmit={(e) => {
                  e.preventDefault();
                  handleReviewCompletion(reviewingRequest.id);
                }} className="review-form">
                  <div className="form-group">
                    <label>최종 확정 무게 (kg)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={finalWeight}
                      onChange={(e) => setFinalWeight(e.target.value ? parseFloat(e.target.value) : '')}
                      placeholder="관리자가 확정하는 최종 무게"
                    />
                    <div className="form-help">
                      작업자가 측정한 무게를 검토하여 최종 무게를 확정하세요.
                    </div>
                  </div>

                  <div className="form-group">
                    <label>검토 메모</label>
                    <textarea
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="검토 결과나 특이사항을 입력하세요 (선택사항)"
                      rows={4}
                      maxLength={1000}
                    />
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => {
                      setReviewingRequest(null);
                      setFinalWeight('');
                      setReviewNotes('');
                    }} className="cancel-btn">
                      취소
                    </button>
                    <button type="submit" className="complete-btn">
                      검토 완료
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* 정기 수거 등록 모달 */}
        {showCreateSchedule && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h2>정기 수거 스케줄 등록</h2>
                <button onClick={() => {
                  setShowCreateSchedule(false);
                  setSelectedUser(null);
                }} className="close-btn">×</button>
              </div>
              
              <div className="modal-content">
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.currentTarget);
                  
                  // 선택된 요일들 가져오기
                  const selectedDays = Array.from(document.querySelectorAll('input[name="days_of_week"]:checked'))
                    .map(checkbox => parseInt((checkbox as HTMLInputElement).value));
                  
                  try {
                    // 각 선택된 요일에 대해 스케줄 생성
                    for (const dayOfWeek of selectedDays) {
                      await api.post('/api/sslmo/auth/admin/recurring-schedules', {
                        user_id: parseInt(formData.get('user_id') as string),
                        day_of_week: dayOfWeek,
                        time_of_day: formData.get('time_of_day'),
                        frequency: formData.get('frequency'),
                        waste_type: '일반쓰레기', // 기본값으로 설정
                        collection_address: formData.get('collection_address'),
                        description: formData.get('description'),
                        contact_phone: selectedUser?.phone || null,
                        special_instructions: formData.get('special_instructions')
                      });
                    }
                    
                    alert('정기 수거 스케줄이 등록되었습니다.');
                    setShowCreateSchedule(false);
                    setSelectedUser(null);
                    loadRecurringSchedules();
                  } catch (error) {
                    console.error('Failed to create schedule:', error);
                    alert('정기 수거 스케줄 등록에 실패했습니다.');
                  }
                }}>
                  <div className="form-group">
                    <label>고객 선택</label>
                    <select 
                      name="user_id" 
                      required
                      value={selectedUser?.id || ''}
                      onChange={(e) => {
                        const userId = parseInt(e.target.value);
                        const user = users.find(u => u.id === userId);
                        setSelectedUser(user || null);
                      }}
                    >
                      <option value="">고객을 선택하세요</option>
                      {users
                        .filter(user => user.role === 'user' && user.is_active)
                        .map(user => (
                          <option key={user.id} value={user.id}>
                            {user.username} ({user.email}) {user.full_name ? `- ${user.full_name}` : ''}
                          </option>
                        ))
                      }
                    </select>
                  </div>

                  <div className="form-group">
                    <label>요일 선택</label>
                    <div className="days-checkboxes">
                      {[
                        { value: 0, label: '월요일' },
                        { value: 1, label: '화요일' },
                        { value: 2, label: '수요일' },
                        { value: 3, label: '목요일' },
                        { value: 4, label: '금요일' },
                        { value: 5, label: '토요일' },
                        { value: 6, label: '일요일' }
                      ].map((day) => (
                        <label key={day.value} className={`day-checkbox ${document.querySelector(`input[name="days_of_week"][value="${day.value}"]:checked`) ? 'selected' : ''}`}>
                          <input
                            type="checkbox"
                            name="days_of_week"
                            value={day.value}
                            onChange={(e) => {
                              const checkboxes = document.querySelectorAll('input[name="days_of_week"]:checked');
                              if (checkboxes.length === 0) {
                                e.target.checked = true; // 최소 하나는 선택되어야 함
                              }
                              // 선택 상태에 따라 클래스 업데이트
                              const label = e.target.closest('.day-checkbox');
                              if (label) {
                                if (e.target.checked) {
                                  label.classList.add('selected');
                                } else {
                                  label.classList.remove('selected');
                                }
                              }
                            }}
                          />
                          <span>{day.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>시간대</label>
                      <select name="time_of_day" required>
                        <option value="">시간대 선택</option>
                        <option value="09:00">오전 9시</option>
                        <option value="10:00">오전 10시</option>
                        <option value="11:00">오전 11시</option>
                        <option value="12:00">오후 12시</option>
                        <option value="13:00">오후 1시</option>
                        <option value="14:00">오후 2시</option>
                        <option value="15:00">오후 3시</option>
                        <option value="16:00">오후 4시</option>
                        <option value="17:00">오후 5시</option>
                        <option value="18:00">오후 6시</option>
                        <option value="19:00">오후 7시</option>
                        <option value="20:00">오후 8시</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>주기</label>
                      <select name="frequency" required>
                        <option value="">주기 선택</option>
                        <option value="weekly">매주</option>
                        <option value="biweekly">격주</option>
                        <option value="monthly">매월</option>
                      </select>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>수거 주소</label>
                    <input 
                      type="text" 
                      name="collection_address" 
                      required 
                      value={selectedUser ? (selectedUser.address_street && selectedUser.address_detail ? 
                        `${selectedUser.address_street} ${selectedUser.address_detail}` : 
                        '') : ''}
                      readOnly={!!(selectedUser && selectedUser.address_street && selectedUser.address_detail)}
                      placeholder={selectedUser ? '주소를 입력하세요' : '상세 주소를 입력하세요'}
                    />
                  </div>

                  <div className="form-group">
                    <label>설명</label>
                    <textarea name="description" placeholder="수거 요청에 대한 상세 설명" rows={3} />
                  </div>

                  <div className="form-group">
                    <label>특별 지시사항</label>
                    <textarea name="special_instructions" placeholder="특별한 지시사항이나 주의사항" rows={2} />
                  </div>

                  <div className="form-actions">
                    <button type="button" onClick={() => {
                      setShowCreateSchedule(false);
                      setSelectedUser(null);
                    }} className="cancel-btn">
                      취소
                    </button>
                    <button type="submit" className="submit-btn">
                      등록
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminRoute>
  );
}